import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCalendarDays, LuCoins, LuFileText, LuFolderKanban, LuGauge, LuHandshake, LuPencil, LuReceipt, LuTag, LuTrash2, LuWallet } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import DetailPane from '../../../components/data/DetailPane';
import useTariffText from '../tariffText';
import { ActiveBadge, initialsOf } from './TariffCell';

const text = (value) => (value === undefined || value === null || value === '' ? '' : String(value));

/**
 * The selected tariff's panel (replaces the old action bar): edit, delete
 * (with a confirm) and every field. Docked beside the table on wide screens,
 * a drawer otherwise; closing it clears the selection.
 */
const TariffDetailPane = ({ docked, height, tariff, position, onPrev, onNext, onClose, onEdit, onDelete }) => {
  const { label, typeLabel, yesNo, formatDate, formatMoney } = useTariffText();
  const paneLabel = label('panel.label');

  if (!tariff) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const project = tariff.projectMissing ? label('table.deletedProject') : text(tariff.projectName);
  const badges = (
    <>
      <ActiveBadge active={tariff.active} />
      {tariff.currency && <Badge tone="neutral">{tariff.currency}</Badge>}
      {tariff.isSubcontractor && (
        <Badge tone={tariff.isSubcontractor === 'Evet' ? 'info' : 'neutral'}>
          <LuHandshake aria-hidden="true" className="tw-h-3 tw-w-3" />
          {label('panel.subcontractor', { answer: yesNo(tariff.isSubcontractor) })}
        </Badge>
      )}
    </>
  );

  const details = [
    { key: 'customer', icon: LuBuilding2, label: label('columns.customer'), value: text(tariff.customerName) },
    { key: 'project', icon: LuFolderKanban, label: label('columns.project'), value: project },
    { key: 'type', icon: LuTag, label: label('columns.type'), value: typeLabel(tariff.type) },
    { key: 'value', icon: LuGauge, label: label('columns.value'), value: text(tariff.value) },
    { key: 'price', icon: LuWallet, label: label('columns.price'), value: text(tariff.price) && tariff.currency ? `${tariff.price} ${tariff.currency}` : text(tariff.price) },
    { key: 'amount', icon: LuReceipt, label: label('columns.amount'), value: formatMoney(tariff.amountValue, tariff.currency) },
    { key: 'currency', icon: LuCoins, label: label('columns.currency'), value: text(tariff.currency) },
    { key: 'date', icon: LuCalendarDays, label: label('columns.date'), value: formatDate(tariff.date) },
    { key: 'subcontractor', icon: LuHandshake, label: label('columns.subcontractor'), value: yesNo(tariff.isSubcontractor) },
    { key: 'info', icon: LuFileText, label: label('columns.info'), value: text(tariff.info) },
  ];

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={tariff.id}
      identity={{
        avatar: initialsOf(tariff.customerName),
        gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30',
        title: `${typeLabel(tariff.type) || label('panel.tariff')}${tariff.value ? ` · ${tariff.value}` : ''}`,
        subtitle: [text(tariff.customerName), project].filter(Boolean).join(' · ') || undefined,
        badges,
      }}
      actions={[
        { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
        { key: 'delete', label: label('panel.delete'), icon: LuTrash2, tint: 'rose', onClick: onDelete },
      ]}
      details={details}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

TariffDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  tariff: PropTypes.object,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default TariffDetailPane;
